import { useMemo } from 'react';
import { useCargoIds } from '@/entities/cargo-id/modules/models/useCargoIds';
import type { ApiCargoId } from '@/shared/types/api/cargo-ids';

export type CargoIdGateStatus =
  | 'loading'
  | 'error'
  | 'approved'
  | 'pending'
  | 'rejected'
  | 'none';

export type CargoIdGate = {
  status: CargoIdGateStatus;
  approvedItem: ApiCargoId | null;
  /** Latest server-side row when status is one of approved/pending/rejected. */
  item: ApiCargoId | null;
  refetch: () => void;
};

/**
 * Derives the parcel-creation gate status from the user's single Cargo ID.
 * - approved: the user has an APPROVED Cargo ID with a code (can create parcels)
 * - pending : the user has a PENDING request but no approved one yet
 * - rejected: the user has a REJECTED request — banner shows the reason + a
 *   resubmit CTA pointing at /ids/request (which seeds the form with the
 *   previously submitted full_name + phone_number)
 * - none    : the user has no Cargo ID at all
 *
 * REJECTED used to collapse into "none" which made the /settings banner
 * render "Tasdiqlanmoqda…" by mistake (Bug 1). The status is now distinct
 * so the banner can render the rejected variant.
 */
export const useCargoIdGateStatus = (): CargoIdGate => {
  const query = useCargoIds();

  return useMemo<CargoIdGate>(() => {
    const refetch = (): void => void query.refetch();
    if (query.isLoading) {
      return { status: 'loading', approvedItem: null, item: null, refetch };
    }
    if (query.isError) {
      return { status: 'error', approvedItem: null, item: null, refetch };
    }
    const item = query.data ?? null;
    if (item && item.status === 'APPROVED' && Boolean(item.code)) {
      return { status: 'approved', approvedItem: item, item, refetch };
    }
    if (item && item.status === 'PENDING') {
      return { status: 'pending', approvedItem: null, item, refetch };
    }
    if (item && item.status === 'REJECTED') {
      return { status: 'rejected', approvedItem: null, item, refetch };
    }
    return { status: 'none', approvedItem: null, item: null, refetch };
  }, [query.isLoading, query.isError, query.data, query]);
};
